
import { Component, Emit, Inject, Prop, Vue, Watch, Mixins } from 'vue-property-decorator';
import MapLoader from './MapLoader.vue';
import { MeasureToolMapFallback } from './measure-tool-map-fallback';
import Loading from 'vue-loading-overlay';
import { 
    getCoordinatesFromFeature, 
    getExtendedBoundsFromFeature,
    removeVertexFromFeature,
    initMapCenter,
    createPolygonLabels,
    imOverPolyVertex,
    normalizeFeatureCollection,
    customPinElement,
    customIconForMarker,
} from '../../utils/map-utils';
import {
    // EventBus,
    // TypeEventBus,
    getUniqueId,
    getRandomColor,
    EventBus,
    TypeEventBus,
} from '../../utils';
import { Destroyed } from '../../decorators';
import ContextMenu from './ContextMenu.vue';
import AddPolygon from './AddPolygon.vue';
import { Bounds, FeatureCollection, MultiPolygon, PolyCoords } from '../../../shared/types/MapShapes';
import { SatellitalGroundOverlays } from '../../../shared/helpers/satellital-ground-overlay';
import ModalNoteInfoMap from '../Notes/ModalNoteInfoMap.vue'
import { InjectionKey } from '../../injection-keys';
import { PolygonEditingMode, TenantConfig } from '../../../tenants/types';
import { LatLngPoint } from '../../utils/point-coordinates';

/** Zoom al centrar en un punto buscado ("Buscar por punto"): nivel de lote. */
const SEARCH_POINT_ZOOM = 15;
@Component({
    components: {
        MapLoader,
        Loading,
        ContextMenu,
        AddPolygon,
        ModalNoteInfoMap
    }
})

/**
 * Mapa de Google con capa de polígonos editable, overlays satelitales, notas y herramienta de medición.
 * Lo que cambia por tenant sale de su config (inyectado): `map.polygonEditing` (cómo se editan los polígonos),
 * `map.fitBoundsPadding` (margen al encuadrar) y `features.satelliteMapByDefault` (vista satelital al iniciar).
 */
export default class SimpleMap extends Vue {
    @Inject({ from: InjectionKey.Tenant, default: null }) readonly tenant!: TenantConfig | null;
    @Prop({ default: null }) readonly overlayImage!: {
        png_url: string;
        bounds?: Bounds;
    } | null;

    private satellitalOverlays = new SatellitalGroundOverlays();
    private measureToolFallback: MeasureToolMapFallback | null = null;

    @Prop({ default: null }) legendUrl!: string | null;
    @Prop({ default: '' }) legendDescription: string;
    @Prop({default: null}) multiPolygon: MultiPolygon;
    @Prop({default: null}) selectedFeaturesIds: Array<number>;
    @Prop({default(){return []}}) selectedItems: Array<object>;
    @Prop({ default: '' }) departmentNameForLimits: string;
    /** El padre muestra un indicador arriba a la izquierda del mapa (centroide): el panel de edición va debajo. */
    @Prop({ default: false }) hasTopLeftOverlay: boolean;
    /** Límites de departamentos a dibujar con el mismo estilo que el `department_polygon` del item. */
    @Prop({ default: null }) departmentsLayer: FeatureCollection | null;
    /**
     * Mapa de selección (listado de lotes): los no seleccionados van tenues y los seleccionados resaltados, para que
     * solo parezcan seleccionados los que lo están. Sin la prop, los estilos de siempre (mapa principal).
     */
    @Prop({ type: Boolean, default: false }) highlightSelection!: boolean;
    visibleAddPolygon: boolean = false;
    googleMapsApiKey: string = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || '';
    @Prop({ default: true }) showPolygon!: boolean;

    @Prop({ default: false }) visibleTagSelector: boolean;
    @Prop({ default: false }) loading: boolean;
    @Prop({ default: false }) polygonsTagsAreVisible: boolean;
    @Prop({ default: false }) savingSmartRoute: boolean;
    @Prop({ default: false }) splitRanchs: boolean;
    @Prop({default: false}) isReadOnly: boolean;
    @Prop({ default: false }) showContextMenuOnGeojson: boolean;
    /** Tipo de mapa inicial; si no se pasa, `hybrid` o `roadmap` según `features.satelliteMapByDefault`. */
    @Prop({ default: null }) mapTypeId: string | null;
    @Prop({ default: '' }) polyName: string;
    @Prop({ default: '500px' }) mapHeight: string;
    @Prop({default: true}) disableSeeInNewTab: boolean;
    /** Nota georreferenciada seleccionada / en hover (compartida con el listado de notas): se resalta su marcador. */
    @Prop({ default: null }) selectedNoteId!: number | null;
    @Prop({ default: null }) hoveredNoteId!: number | null;
    initialDataLayer: google.maps.Data | null = null;
    initZoom: number = 7;
    mapConfig: any = {
        zoom: this.initZoom,
        center: initMapCenter,
        mapTypeId: this.getMapType,
        mapId: 'INSURANCE_MAP',
    };
    //ctrl mac listeners
    isMac: boolean = false;
    webkit: boolean = false;
    mozilla: boolean = false;
    opera: boolean = false;
    kC: string = '';
    selectingRanchs: boolean = false;
    editPolygon: boolean = false;
    ranchsSelected: Array<object> = [];
    markersPositions: Array< google.maps.marker.AdvancedMarkerElement> = [];
    activeInfoWindows: any = [];
    colorGreen: string = '#0096cc';
    satellitalPolygonStyles: object = {
        strokeColor: '#FFFFFF',
        strokeWeight: 3,
        fillColor: '#00FFFF',
        fillOpacity: 0.15,
        zIndex: 100
    }
    defaultPolygonStyles: object = {
        strokeColor: '#000000',
        strokeWeight: 2,
        fillColor: '#adb3e6',
        fillOpacity: 0.4,
        zIndex: 2
    };
    mouseOverPolygonStyles: object = {
        strokeWeight: 2,
        fillColor: '#42a6e2'
    };
    multiplePolygonsSelectedStyles: object = {
        strokeWeight: 1,
        fillColor: this.colorGreen
    };
    unselectedPolylineOptions: object = {
        strokeOpacity: 0.2
    };
    selectedPolylineOptions: object = {
        strokeOpacity: 1,
        fillColor: this.colorGreen,
        strokeColor: '#ffffff'
    };
    unselectedSatellitalStyles: object = {
        strokeColor: '#FFFFFF',
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: '#FFFFFF',
        fillOpacity: 0.25,
        zIndex: 2
    };
    unselectedDefaultStyles: object = {
        strokeColor: '#000000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#adb3e6',
        fillOpacity: 0.35,
        zIndex: 2
    };
    highlightedSelectionStyles: object = {
        strokeColor: '#FFFFFF',
        strokeOpacity: 1,
        strokeWeight: 4,
        fillColor: this.colorGreen,
        fillOpacity: 0.6,
        zIndex: 10
    };
    departmentStyles: object = {
        strokeColor: '#ff0000',
        fillOpacity: 0
    }
    previousBounds: any = {};
    showContextMenu: boolean = true;
    mapMounted: boolean = false;
    
    polyCoords: any = [];
    savedPolygonsOnMemory: Array<google.maps.Polygon> = [];
    previousFeatureCoords: {
        coords: google.maps.Data.Geometry | null
    } = {
        coords: null
    };
    drawingManager: any = null;
    markerAdded: google.maps.marker.AdvancedMarkerElement | null = null;
    selectedRanchs: Array<google.maps.Data.Feature> = [];
    featuresGeojsonToExport: any = [];
    doNotTriggerTwiceEvt: any = null;
    doNotTriggerTwiceTimeout: any = null;
    boundsChanged: boolean = false;
    departmentPolygonLayer: google.maps.Data | null = null;
    /** Capa de `departmentsLayer` (departamentos del listado), aparte de la del departamento del item. */
    listDepartmentsLayer: google.maps.Data | null = null;
    lastComparedGeoJson: any = null;
    mapIsSatellital: boolean = false;

    hoverTimer: number | null = null;
    hoverListener: google.maps.MapsEventListener | null = null;
    leaveListener: google.maps.MapsEventListener | null = null;
    hoverEvent: MouseEvent | null = null;
    deleteModeListener: google.maps.MapsEventListener | null = null;
    vertexDeleteRightclickListener: google.maps.MapsEventListener | null = null;
    lastDeleteClickTime: number = 0;
    lastDeleteClickFeature: google.maps.Data.Feature | null = null;
    lastDeleteClickPolygonIndex: number = -1;
    contextMenuItem: Array<{ icon?: any; title: string; event: { name: string; data: any } }> = [];
    ranchNoteMarkers: Array<google.maps.marker.AdvancedMarkerElement> = [];
    ranchNoteMarkersById: Map<number, google.maps.marker.AdvancedMarkerElement> = new Map();
    noteSelectionMapListener: google.maps.MapsEventListener | null = null;
    /** Marcador temporal de "Buscar por punto". No es un polígono: no se guarda ni se selecciona. */
    searchPointMarker: google.maps.marker.AdvancedMarkerElement | null = null;
    visibleModalRanchNoteInfo: boolean = false;
    ranchNoteInfo: object | null = null;
    modifiedCounter: number = 0;
    deleteMode: boolean = false;
    polygonsMarkedForDeletion: Array<google.maps.Data.Feature> = [];
    polygonsMarkedForDeletionIds: Set<string> = new Set();


    get mapLoader(): HTMLFormElement {
        return this.$refs.customMap as HTMLFormElement;
    }

    get googleMap(): google.maps.Map {
        return this.mapLoader.mapObject;
    }

    get getMapType(): string {
        if (this.mapTypeId) {
            return this.mapTypeId;
        }
        return this.satelliteByDefault ? 'hybrid' : 'roadmap';
    }

    get satelliteByDefault(): boolean {
        return !!this.tenant && this.tenant.features.satelliteMapByDefault;
    }

    /** `PolygonEditingMode.Selection` (default sin tenant) o `PolygonEditingMode.ContextMenu`. */
    get polygonEditingMode(): PolygonEditingMode {
        return this.tenant ? this.tenant.map.polygonEditing : PolygonEditingMode.Selection;
    }

    /** Modo "Seleccionar polígonos": MultiPolygon separados en hermanos, menú reducido, borrar vértice con click derecho. */
    get selectionEditing(): boolean {
        return this.polygonEditingMode === PolygonEditingMode.Selection;
    }

    /** Modo menú contextual: "Modificar Coordenadas"/"Eliminar Polígono" y reencuadre después de guardar/borrar. */
    get contextMenuEditing(): boolean {
        return this.polygonEditingMode === PolygonEditingMode.ContextMenu;
    }

    @Watch('showPolygon', { immediate: true })
    onShowPolygonChange(newVal: boolean) {
        if (!this.mapMounted) return;

        this.googleMap.data.forEach((feature: google.maps.Data.Feature) => {
            const isPolygonLayer = feature.getProperty('layerType') === 'polygon';
            if (isPolygonLayer) {
            this.googleMap.data.overrideStyle(feature, {
                visible: newVal,
                ...this.currentPolygonStyle,
            });
            }
        });
    }

    @Watch('overlayImage', { immediate: true })
    onOverlayImageChange(newImage) {
    if (!this.mapMounted || !this.googleMap) return;

    if (!newImage || !newImage.png_url || !newImage.bounds) return;
    // La tira también llama a setGroundOverlayInMap con la misma URL.
    // show() no crea una segunda copia.
    this.setGroundOverlayInMap(newImage.png_url, newImage.bounds, false);
    }

    @Watch('selectedNoteId')
    onSelectedNoteIdChange(newId: number | null) {
        this.applyNoteMarkersHighlight();
        if (newId != null) this.panToNoteMarkerIfOutOfView(newId);
    }

    @Watch('hoveredNoteId')
    onHoveredNoteIdChange() {
        this.applyNoteMarkersHighlight();
    }

    @Emit()
    selectNote(noteId: number | null) {}

    @Emit()
    onPolygonSave(
        coords: PolyCoords
    ){}


    @Emit()
    userCancelPolygon() {}

    @Emit()
    startModifyPolygon(feature: google.maps.Data.Feature) {}

    /**
     * Capa de límites de departamento (estilo `departmentStyles`) sobre el mapa. Deja pasar clics y clic derecho al
     * mapa para que la regla de medición siga funcionando encima del límite.
     */
    createDepartmentLayer(geojson: MultiPolygon | FeatureCollection, clickable: boolean = true): google.maps.Data {
        const layer = new google.maps.Data();
        layer.setStyle({ ...this.departmentStyles, clickable });
        layer.addGeoJson(geojson);
        layer.setMap(this.googleMap);

        // Allow mouse events to pass through to the measure tool
        // The department polygon Data layer intercepts mouse events, so we need to
        // explicitly allow clicks to propagate to the map/measure tool
        
        // Handle right-clicks for the measure tool context menu
        layer.addListener('rightclick', (e: google.maps.Data.MouseEvent) => {
            // Trigger the map's rightclick event so the measure tool can handle it
            if (e.latLng && e.domEvent) {
                google.maps.event.trigger(this.googleMap, 'rightclick', {
                    latLng: e.latLng,
                    domEvent: e.domEvent
                });
                
                // Also ensure the native contextmenu event reaches the map container
                // The measure tool needs the native DOM event to work properly
                const mapDiv = this.googleMap.getDiv();
                if (mapDiv && e.domEvent && e.domEvent instanceof MouseEvent) {
                    // Create a new contextmenu event at the map container level
                    const contextMenuEvent = new MouseEvent('contextmenu', {
                        bubbles: true,
                        cancelable: true,
                        clientX: e.domEvent.clientX,
                        clientY: e.domEvent.clientY,
                        button: 2,
                        buttons: 2
                    });
                    mapDiv.dispatchEvent(contextMenuEvent);
                }
            }
        });
        
        // Handle left-clicks so the measure tool can continue measuring
        // The measure tool needs clicks to add points to the measurement
        layer.addListener('click', (e: google.maps.Data.MouseEvent) => {
            // Allow clicks to propagate to the map so the measure tool can handle them
            if (e.latLng && e.domEvent) {
                google.maps.event.trigger(this.googleMap, 'click', {
                    latLng: e.latLng,
                    domEvent: e.domEvent
                });
            }
        });

        return layer;
    }

    /** Dibuja (o redibuja) los departamentos de `departmentsLayer`, p. ej. los del listado de items precargados. */
    drawDepartmentsLayer() {
        if (this.listDepartmentsLayer) {
            this.listDepartmentsLayer.setMap(null);
            this.listDepartmentsLayer = null;
        }
        if (!this.mapMounted || !this.googleMap || !this.departmentsLayer) {
            return;
        }
        // No clickable: los clics llegan a los lotes de abajo (selección en el mapa) y a la regla de medición.
        this.listDepartmentsLayer = this.createDepartmentLayer(this.departmentsLayer, false);
    }

    @Watch('departmentsLayer')
    onDepartmentsLayerChange() {
        this.drawDepartmentsLayer();
    }

    showDepartmentLimits(polygon: MultiPolygon){
        let geoData = this.createDepartmentLayer(polygon);
        this.departmentPolygonLayer = geoData;

        let bounds: google.maps.LatLngBounds | null = null;
        

        setTimeout(() => {
            geoData.forEach((feature: google.maps.Data.Feature) => {
                bounds = getExtendedBoundsFromFeature(feature, bounds);
            })
            let areFeatures = this.areFeaturesInMap();

            if(!areFeatures && bounds){
                this.fitBounds(bounds);
                this.setCenter(bounds)
            }
        }, 500);

    }

    areFeaturesInMap(): boolean {
        let areFeaturesInMap = false;
        this.googleMap.data.forEach((feature: google.maps.Data.Feature) => {
            areFeaturesInMap = true;
            return
        })
        return areFeaturesInMap
    }

    

    async clickModifyPolygonFromMenu(feature: google.maps.Data.Feature) {
        this.removeEditableFromAllFeatures();
        await this.saveCurrentDataLayer();
        this.showModalEditPolygon();
        this.modifyPolygon(feature);
        this.startModifyPolygon(feature);
    }

    clickModifyPolygonCoordinates(feature: google.maps.Data.Feature){
        EventBus.$emit(TypeEventBus.EDIT_COORDINATES, feature)
    }

    clickDeletePolygonFromMenu(feature: google.maps.Data.Feature) {
        this.googleMap.data.remove(feature);
        if (this.contextMenuEditing) {
            this.fitMapToCurrentFeatures();
            this.savePolygon();
        } else {
            this.renormalizeDataLayer();
            // Don't save to backend here - only save when user clicks "Guardar"
        }
        this.addIndexTagsToAllPolygons();  
    }

    enterDeleteMode() {
        this.deleteMode = true;
        this.polygonsMarkedForDeletion = [];
        this.polygonsMarkedForDeletionIds.clear();
        this.removeEditableFromAllFeatures(); // Disable editing when in delete mode
        this.setupDeleteModeListeners();
    }

    enterSelectionModeForCreateItem() {
        this.deleteMode = true;
        this.polygonsMarkedForDeletion = [];
        this.polygonsMarkedForDeletionIds.clear();
        this.removeEditableFromAllFeatures();
        this.setupDeleteModeListeners();
    }

    exitDeleteMode() {
        this.deleteMode = false;
        this.clearDeleteModeStyles();
        this.polygonsMarkedForDeletion = [];
        this.polygonsMarkedForDeletionIds.clear();
        this.removeDeleteModeListeners();
        this.lastDeleteClickTime = 0;
        this.lastDeleteClickFeature = null;
        this.lastDeleteClickPolygonIndex = -1;
        
        // Restore editable state if still in edit mode
        if (this.editPolygon && this.selectedRanchs.length > 0) {
            // Make all selected features and their siblings editable
            const allFeaturesToEdit = new Set<google.maps.Data.Feature>();
            this.selectedRanchs.forEach(feature => {
                const siblings = this.getSiblingFeatures(feature);
                siblings.forEach(sibling => allFeaturesToEdit.add(sibling));
            });
            
            allFeaturesToEdit.forEach(feature => {
                this.updateFeatureEditableProp(feature, true);
            });
        }
    }

    setupDeleteModeListeners() {
    if (this.deleteModeListener) {
        this.deleteModeListener.remove();
    }

    this.deleteModeListener = this.googleMap.data.addListener(
        'click',
        (e: google.maps.Data.MouseEvent) => {
        if (!this.deleteMode) return;

        if (e.domEvent) {
            e.domEvent.preventDefault();
            e.domEvent.stopPropagation();
        }

        const feature = e.feature;
        if (!feature) return;

        const customId = feature.getProperty('customId');
        if (!customId) return;
        
        const customIdStr = String(customId);

        // Toggle delete using ID-based tracking
        if (this.polygonsMarkedForDeletionIds.has(customIdStr)) {
            // UNMARK
            this.polygonsMarkedForDeletionIds.delete(customIdStr);
            const index = this.polygonsMarkedForDeletion.findIndex(
                f => String(f.getProperty('customId')) === customIdStr
            );
            if (index > -1) {
                this.polygonsMarkedForDeletion.splice(index, 1);
            }
            this.googleMap.data.revertStyle(feature);
        } else {
            // MARK
            this.polygonsMarkedForDeletionIds.add(customIdStr);
            this.polygonsMarkedForDeletion.push(feature);
            this.googleMap.data.overrideStyle(feature, {
            fillColor: '#0d7ea2',
            fillOpacity: 0.6,
            strokeColor: '#0d7ea2',
            strokeWeight: 2,
            });
        }
        }
    );
    }

    removeDeleteModeListeners() {
        if (this.deleteModeListener) {
            this.deleteModeListener.remove();
            this.deleteModeListener = null;
        }
    }

    updatePolygonDeleteStyle(feature: google.maps.Data.Feature, polygonIndex: number, markForDeletion: boolean) {
        const geometry = feature.getGeometry();
        if (!geometry) return;

        const featureId = feature.getProperty('customId');
        
        // Check if this feature has any polygons marked for deletion
        const hasMarkedPolygons = this.polygonsMarkedForDeletion.some(item => 
            item.getProperty('customId') === featureId
        );

        // Since Google Maps Data layer doesn't support per-polygon styling within a MultiPolygon,
        // we'll highlight the entire feature if any polygon within it is marked
        // The actual deletion will still only remove the specific clicked polygons
        if (hasMarkedPolygons) {
            this.googleMap.data.overrideStyle(feature, {
                fillColor: '#0d7ea2',
                fillOpacity: 0.5,
                strokeColor: '#0d7ea2',
                strokeWeight: 3,
                strokeOpacity: 1
            });
        } else {
            this.googleMap.data.overrideStyle(feature, this.currentPolygonStyle);
        }
    }

    applyDeleteModeStyle(feature: google.maps.Data.Feature) {
        this.googleMap.data.overrideStyle(feature, {
            fillColor: '#0d7ea2',
            fillOpacity: 0.5,
            strokeColor: '#0d7ea2',
            strokeWeight: 3,
            strokeOpacity: 1
        });
    }

    resetDeleteModeStyle(feature: google.maps.Data.Feature) {
        this.googleMap.data.overrideStyle(feature, this.currentPolygonStyle);
    }

    clearDeleteModeStyles() {
        this.googleMap.data.forEach((feature: google.maps.Data.Feature) => {
            this.resetDeleteModeStyle(feature);
        });
    }

    confirmDeletePolygons() {
        if (this.polygonsMarkedForDeletionIds.size === 0) {
            return;
        }

        // Remove all marked features using the ID set
        const featuresToRemove: google.maps.Data.Feature[] = [];
        this.googleMap.data.forEach((feature: google.maps.Data.Feature) => {
            const customId = feature.getProperty('customId');
            if (customId && this.polygonsMarkedForDeletionIds.has(String(customId))) {
                featuresToRemove.push(feature);
            }
        });

        featuresToRemove.forEach(feature => {
            this.googleMap.data.remove(feature);
        });

        this.polygonsMarkedForDeletion = [];
        this.polygonsMarkedForDeletionIds.clear();
        this.exitDeleteMode();
        this.renormalizeDataLayer();
        // Don't save to backend here - only save when user clicks "Guardar"
        this.addIndexTagsToAllPolygons();
    }

    confirmKeepOnlySelected() {
        if (this.polygonsMarkedForDeletionIds.size === 0) {
            return;
        }

        // Remove every feature whose customId is NOT in the selected set
        const featuresToRemove: google.maps.Data.Feature[] = [];
        this.googleMap.data.forEach((feature: google.maps.Data.Feature) => {
            const customId = feature.getProperty('customId');
            if (!customId || !this.polygonsMarkedForDeletionIds.has(String(customId))) {
                featuresToRemove.push(feature);
            }
        });

        featuresToRemove.forEach(feature => {
            this.googleMap.data.remove(feature);
        });

        this.polygonsMarkedForDeletion = [];
        this.polygonsMarkedForDeletionIds.clear();
        this.exitDeleteMode();
        this.renormalizeDataLayer();
        this.addIndexTagsToAllPolygons();
    }

    buildMultiPolygonFeature(features: google.maps.Data.Feature[]) {
        if (features.length === 0) return null;
        
        const properties: any = {};
        features[0].forEachProperty((value, key) => {
            if (key !== 'parentId' && key !== 'polygonIndex') {
                properties[key] = value;
            }
        });

        return {
            type: 'Feature',
            properties: properties,
            geometry: {
                type: 'MultiPolygon',
                coordinates: features
                    .sort((a, b) => {
                        const indexA = a.getProperty('polygonIndex');
                        const indexB = b.getProperty('polygonIndex');
                        const numA = typeof indexA === 'number' ? indexA : 0;
                        const numB = typeof indexB === 'number' ? indexB : 0;
                        return numA - numB;
                    })
                    .map(feature => {
                        const geometry = feature.getGeometry() as google.maps.Data.Polygon;
                        if (!geometry) return [];
                        return geometry
                            .getArray()
                            .map(ring =>
                                ring.getArray().map(coord => [coord.lng(), coord.lat()])
                            );
                    }),
            },
        };
    }

    rebuildMultiPolygonsFromMap(): any[] {
        const grouped: Record<string, google.maps.Data.Feature[]> = {};

        this.googleMap.data.forEach(feature => {
            const parentId = feature.getProperty('parentId');
            if (!parentId) return;

            const parentIdStr = String(parentId);
            if (!grouped[parentIdStr]) {
                grouped[parentIdStr] = [];
            }

            grouped[parentIdStr].push(feature);
        });

        return Object.values(grouped)
            .map(features => this.buildMultiPolygonFeature(features))
            .filter(f => f !== null) as any[];
    }



    clickAddNewPolygon(){
        this.enableMapDrawingTools();
    }
    
    @Emit()
    seeInNewTab(feature: google.maps.Data.Feature) {}

    closeContextMenu() {
        this.showContextMenu = false;
    }

    
    sayWho() {
        let ua = navigator.userAgent,
            tem,
            M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(M[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return { browser: 'IE', version: tem[1] || '' };
        }
        if (M[1] === 'Chrome') {
            tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
            //if(tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
            if (tem != null) return { browser: tem.slice(1)[0].replace('OPR', 'Opera'), version: tem.slice(1)[1] };
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
        if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1]);
        return { browser: M[0], version: M[1] };
    }

    //Proceso el poligono luego de que el usuario lo dibujo
    handleNewHandWrittenPolygon(newPolygon: google.maps.Polygon) {
        const newPath = newPolygon.getPath().getArray().map(coord => ({
            lat: coord.lat(),
            lng: coord.lng()
        }));

        if (!this.areFeaturesInMap()) {
            const newFeature = new google.maps.Data.Feature({
                geometry: new google.maps.Data.MultiPolygon([[
                    new google.maps.Data.LinearRing(newPath.map(coord =>
                        new google.maps.LatLng(coord.lat, coord.lng)
                    ))
                ]]),
                properties: {
                    customId: getUniqueId()
                }
            });
            this.addFeatureToDataLayer(newFeature);
            this.renormalizeDataLayer();
            return
        }

        let isUpdated = false;

        this.googleMap.data.forEach(feature => {
            const geometry = feature.getGeometry();

            if (!geometry) {
                return;
            }

            let multiPolygon: google.maps.Data.MultiPolygon;

            // Convertimos Polygon a MultiPolygon si es necesario
            if (geometry.getType() === 'Polygon') {
                const polygon = geometry as google.maps.Data.Polygon;
                const rings = polygon.getArray();
                multiPolygon = new google.maps.Data.MultiPolygon([rings]);
                feature.setGeometry(multiPolygon); // Guardar la conversión
            } else if (geometry.getType() === 'MultiPolygon') {
                multiPolygon = geometry as google.maps.Data.MultiPolygon;
            } else {
                return;
            }

            const polygons = multiPolygon.getArray();

            polygons.forEach((polygon, index) => {
                const rings = polygon.getArray() as google.maps.Data.LinearRing[];
                const outerRing = rings[0];
                const outerPath = outerRing.getArray();

                const tempPolygon = new google.maps.Polygon({
                    paths: outerPath.map(coord => ({ lat: coord.lat(), lng: coord.lng() }))
                });

                // Verificar si el nuevo polígono está completamente contenido
                const isFullyContained = newPath.every(coord =>
                    google.maps.geometry.poly.containsLocation(
                        new google.maps.LatLng(coord.lat, coord.lng),
                        tempPolygon
                    )
                );

                if (isFullyContained) {
                    // Agregar el nuevo agujero
                    const newHole = new google.maps.Data.LinearRing(
                        newPath.map(coord => new google.maps.LatLng(coord.lat, coord.lng))
                    );
                    rings.push(newHole);

                    // Actualizar la geometría
                    polygons[index] = new google.maps.Data.Polygon(rings);
                    feature.setGeometry(new google.maps.Data.MultiPolygon(polygons));
                    isUpdated = true;
                }
            });
        });

        if (!isUpdated) {
            const newFeature = new google.maps.Data.Feature({
                geometry: new google.maps.Data.MultiPolygon([[
                    new google.maps.Data.LinearRing(newPath.map(coord =>
                        new google.maps.LatLng(coord.lat, coord.lng)
                    ))
                ]]),
                properties: {
                    customId: getUniqueId()
                }
            
            });
            this.addFeatureToDataLayer(newFeature)
        }
        
        // Re-normalize after any polygon modification to ensure consistent structure
        this.renormalizeDataLayer();
    }

    addFeatureToDataLayer(feature: google.maps.Data.Feature) {
        this.googleMap.data.add(feature);
        this.updateFeatureEditableProp(feature);
        this.addStylesToAllFeatures();
    }

    // Re-normalize the data layer to ensure all MultiPolygons are split into individual Polygons
    renormalizeDataLayer() {
        // Solo en modo selección: en modo menú contextual los MultiPolygon se editan como una sola feature
        if (!this.selectionEditing) {
            return;
        }
        // Don't renormalize while in delete mode to avoid losing feature references
        if (this.deleteMode) {
            return;
        }

        this.googleMap.data.toGeoJson((currentGeoJson: any) => {
            // Normalize the GeoJSON to split MultiPolygons
            const normalizedGeoJson = normalizeFeatureCollection(currentGeoJson);
            
            // Clear the current data layer
            const featuresToRemove: google.maps.Data.Feature[] = [];
            this.googleMap.data.forEach(feature => {
                featuresToRemove.push(feature);
            });
            featuresToRemove.forEach(feature => {
                this.googleMap.data.remove(feature);
            });
            
            // Re-add the normalized features
            this.googleMap.data.addGeoJson(normalizedGeoJson, {
                idPropertyName: 'customId'
            });
            
            // Restore the styles
            this.addStylesToAllFeatures();
        });
    }
    


    addCtrlListeners() {
        let self = this;
        this.webkit = this.sayWho().browser === 'Chrome' || this.sayWho().browser === 'Safari';
        this.mozilla = this.sayWho().browser === 'Firefox';
        this.opera = this.sayWho().browser === 'Opera';
        window.addEventListener('keydown', event => {
            let kC = event.keyCode;
            if (
                event.key === 'Control' ||
                ((this.webkit || this.opera) && (kC === 91 || kC === 93)) ||
                (this.mozilla && kC === 224)
            ) {
                this.selectingRanchs = true;
            }
        });

        window.addEventListener('keyup', event => {
            let kC = event.keyCode;
            if (
                event.key === 'Control' ||
                ((this.webkit || this.opera) && (kC === 91 || kC === 93)) ||
                (this.mozilla && kC === 224)
            ) {
                this.selectingRanchs = false;
            }
        });

        window.addEventListener(
            'blur',
            event => {
                this.selectingRanchs = false;
                this.ranchsSelected = [];
            },
            false
        );
    }

   

    visibilityAddPolygonModal(value) {
        this.visibleAddPolygon = value;
        this.$emit('edit-polygon-modal-visibility', value);
    }


    addGeoJsonClickEvent() {
        let self = this;
        this.googleMap.data.addListener('click', function(evt) {
            // Skip if in delete mode - delete mode has its own click handler
            if (self.deleteMode || (self.measureToolFallback && self.measureToolFallback.isActive())) {
                return;
            }
            
            clearTimeout(self.doNotTriggerTwiceTimeout);
            self.doNotTriggerTwiceTimeout = setTimeout(function(){
                let featureClicked = evt.feature;
                // En un mapa de selección (`highlightSelection`) el click alterna la selección sin Ctrl/Cmd.
                const controlIsPressed = self.highlightSelection || evt.domEvent.ctrlKey || evt.domEvent.metaKey;

                let ranchIsSelected = featureClicked.getProperty('selected') || false;
               
                if(controlIsPressed && !ranchIsSelected){
                    self.addFeatureToSelectedOnes(featureClicked);
                }else{
                    self.unselectFeature(featureClicked)
                    
                }
                self.exportSelectedRanchs();

                

            }, 100);
            
        });
    }
    

    addGeoJsonHoverListener() {
    if (this.hoverListener) {
        this.hoverListener.remove();
    }

    if(this.isReadOnly) return

    this.hoverListener = this.googleMap.data.addListener('mouseover', (e: google.maps.Data.MouseEvent) => {
        if (!this.showContextMenuOnGeojson || this.isReadOnly || this.deleteMode) return;

        if (this.contextMenuEditing) {
            const overVertex = imOverPolyVertex(e);
            const isEditingThisFeature = !!(e.feature && e.feature.getProperty('editing_feature'));

            // Permitir menú por hover en modo selección; no mostrarlo sobre el polígono en edición
            if (overVertex || isEditingThisFeature) {
                if (isEditingThisFeature) {
                    this.hoverEvent = null;
                }
                return;
            }
        } else if (imOverPolyVertex(e) || this.editPolygon) {
            return;
        }

        const feature = e.feature!;
        this.contextMenuItem = this.contextMenuEditing
            ? [
                {
                    icon: { value: 'mdi-pencil', color: 'info' },
                    title: 'Modificar Polígono',
                    event: { name: 'modify-ranch', data: feature }
                },
                {
                    icon: { value: 'mdi-vector-polygon', color: 'primary' },
                    title: 'Modificar Coordenadas',
                    event: { name: 'modify-coordinates', data: feature }
                },
                {
                    icon: { value: 'mdi-delete', color: 'error' },
                    title: 'Eliminar Polígono',
                    event: { name: 'delete-ranch', data: feature }
                }
            ]
            : [
                {
                    icon: { value: 'mdi-pencil', color: 'info' },
                    title: 'Modificar Polígono',
                    event: { name: 'modify-ranch', data: feature }
                }
            ];

        if (this.hoverTimer !== null) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = null;
        }
            this.hoverTimer = window.setTimeout(() => {
                this.hoverEvent = e.domEvent as MouseEvent;
                this.hoverTimer = null;
            }, 150);
        });
    }

    addVertexDeleteRightclickListener() {
        this.removeVertexDeleteRightclickListener();
        if (!this.selectionEditing) {
            return;
        }
        const self = this;
        this.vertexDeleteRightclickListener = this.googleMap.data.addListener('rightclick', (event: google.maps.Data.MouseEvent) => {
            if (!self.editPolygon || self.deleteMode) return;
            if (!self.selectedRanchs.includes(event.feature!)) return;
            if (!imOverPolyVertex(event)) return;
            removeVertexFromFeature(event);
            if (event.domEvent) event.domEvent.preventDefault();
        });
    }

    removeVertexDeleteRightclickListener() {
        if (this.vertexDeleteRightclickListener) {
            this.vertexDeleteRightclickListener.remove();
            this.vertexDeleteRightclickListener = null;
        }
    }

    updateFeatureEditableProp(feature: google.maps.Data.Feature, needEdition: boolean = true) {
        //Agrego los handlers al feature para poder modificarlo
        feature.setProperty('editing_feature', needEdition);
        this.googleMap.data.overrideStyle(feature, {
            editable: needEdition,
        });
    }

    areEditingFeatures(): boolean {
        let found = false;
        this.googleMap.data.forEach((feature) => {
            if (feature.getProperty('editing_feature')) {
                found = true;
            }
        });
        return found;
    }


    //Guardo el datalayer actual para posible rollback
    saveCurrentDataLayer(): Promise<void> {
        return new Promise((resolve) => {
            this.googleMap.data.toGeoJson((geoJson) => {
                this.initialDataLayer = JSON.parse(JSON.stringify(geoJson));
                resolve();
            });
        });
    }

    getCurrentFeatureCollection(): Promise<FeatureCollection> {
        return new Promise((resolve) => {
            if (!this.googleMap || !this.googleMap.data) {
                resolve({ type: 'FeatureCollection', features: [] });
                return;
            }
            this.googleMap.data.toGeoJson((geoJson) => {
                resolve(JSON.parse(JSON.stringify(geoJson)));
            });
        });
    }

    // Get all features that share the same parentId (sibling polygons from a split MultiPolygon)
    getSiblingFeatures(feature: google.maps.Data.Feature): google.maps.Data.Feature[] {
        const parentId = feature.getProperty('parentId');
        
        // If no parentId, this is a standalone feature
        if (!parentId) {
            return [feature];
        }
        
        // Find all features with the same parentId
        const siblings: google.maps.Data.Feature[] = [];
        this.googleMap.data.forEach((f: google.maps.Data.Feature) => {
            if (f.getProperty('parentId') === parentId) {
                siblings.push(f);
            }
        });
        
        return siblings.length > 0 ? siblings : [feature];
    }

    async modifyPolygon(feature: google.maps.Data.Feature) {
        let self = this;
        await this.saveCurrentDataLayer();
        this.showModalEditPolygon();
        
        // Get all sibling features (parts of the same original MultiPolygon)
        const siblingFeatures = this.getSiblingFeatures(feature);
        
        // Make all sibling features editable and add to selectedRanchs
        siblingFeatures.forEach(siblingFeature => {
            if (!this.selectedRanchs.includes(siblingFeature)) {
                this.selectedRanchs.push(siblingFeature);
            }
            this.updateFeatureEditableProp(siblingFeature);
        });
        this.addVertexDeleteRightclickListener();
    }

    showModalEditPolygon() {
        this.editPolygon = true;
        EventBus.$emit(TypeEventBus.HAND_WRITING_DISABLED_BUTTON, true);
        this.visibilityAddPolygonModal(true);
    }

    //Coordenadas del Data Layer para Enviar al Backend
    getCoordsFromDataLayer(): PolyCoords {
        // Group features by parentId to rebuild MultiPolygons
        const grouped: Record<string, google.maps.Data.Feature[]> = {};
        const standaloneFeatures: google.maps.Data.Feature[] = [];

        this.googleMap.data.forEach((feature) => {
            const parentId = feature.getProperty('parentId');
            if (parentId) {
                const parentIdStr = String(parentId);
                if (!grouped[parentIdStr]) {
                    grouped[parentIdStr] = [];
                }
                grouped[parentIdStr].push(feature);
            } else {
                standaloneFeatures.push(feature);
            }
        });

        let coordinates: PolyCoords = [];

        // Process grouped features (rebuild MultiPolygons)
        Object.values(grouped).forEach(features => {
            if (features.length === 0) return;
            
            // Sort by polygonIndex to maintain order
            features.sort((a, b) => {
                const indexA = a.getProperty('polygonIndex');
                const indexB = b.getProperty('polygonIndex');
                const numA = typeof indexA === 'number' ? indexA : 0;
                const numB = typeof indexB === 'number' ? indexB : 0;
                return numA - numB;
            });

            // Build MultiPolygon coordinates from grouped features
            const multiPolygonCoords: any[] = [];
            features.forEach(feature => {
                const geometry = feature.getGeometry();
                if (geometry && geometry.getType() === 'Polygon') {
                    const polygon = geometry as google.maps.Data.Polygon;
                    const rings = polygon.getArray();
                    const polygonCoords = rings.map(ring => 
                        ring.getArray().map(coord => ({
                            lat: coord.lat(),
                            lng: coord.lng()
                        }))
                    );
                    multiPolygonCoords.push(polygonCoords);
                }
            });

            if (multiPolygonCoords.length > 0) {
                coordinates.push(multiPolygonCoords);
            }
        });

        // Process standalone features (not part of a MultiPolygon)
        standaloneFeatures.forEach((feature) => {
            const geometry = feature.getGeometry();
            if (geometry) {
                const featureCoords = getCoordinatesFromFeature(feature);
                coordinates = [...coordinates, ...featureCoords];
            }
        });

        return coordinates;
    }

    /** Coordinates of only the polygons currently marked for selection (e.g. in "Crear Item" flow). Same format as getCoordsFromDataLayer. */
    getCoordsFromMarkedPolygons(): PolyCoords {
        let coordinates: PolyCoords = [];
        this.polygonsMarkedForDeletion.forEach((feature) => {
            const featureCoords = getCoordinatesFromFeature(feature);
            coordinates = [...coordinates, ...featureCoords];
        });
        return coordinates;
    }

    /** Number of polygons currently marked for selection. Used by "Crear Item" modal to enable/disable Save. */
    getMarkedPolygonsCount(): number {
        return this.polygonsMarkedForDeletion.length;
    }

    removeFeatureFromMap(feature: google.maps.Data.Feature) {
        this.googleMap.data.remove(feature);
    }

    get currentPolygonStyle() {
        return this.polygonStyleFor(this.getMapType);
    }


    /** Estilo base de los polígonos según el tipo de mapa (`highlightSelection`: el de no seleccionado). */
    polygonStyleFor(mapType: string | null | undefined): object {
        if (this.highlightSelection) {
            return mapType === 'hybrid' ? this.unselectedSatellitalStyles : this.unselectedDefaultStyles;
        }
        return mapType === 'hybrid' ? this.satellitalPolygonStyles : this.defaultPolygonStyles;
    }

    refreshPolygonStyles() {
        const style = this.polygonStyleFor(this.googleMap.getMapTypeId());

        this.googleMap.data.setStyle(function(feature) {
            return style
        });
    }

    //Le agrego estilos a cada feature del Data Layer
    addStylesToAllFeatures() {
        let self = this;
        this.googleMap.data.setStyle(function(feature) {
            return self.currentPolygonStyle;
        });
    }

    addBoundsChangedListener() {
        let self = this;
        this.googleMap.addListener('bounds_changed', function() {
            let currentBounds = self.googleMap.getBounds();
            self.previousBounds = currentBounds;
        });
    }


    //Agrego un nuevo geojson al Mapa
    async addGeoJson(geoJson: FeatureCollection) {
        let self = this;
        this.boundsChanged = false;
        if(!this.googleMap.data){
            return
        }
        this.removeFeaturesFromDataLayer();
        try{

            let updatedGeoJson = {
                ...geoJson,
                features: geoJson.features.map((feature, index) => {
                    if (!this.selectionEditing) {
                        // Modo menú contextual: ids nuevos siempre (al sumar colecciones no se pisan features)
                        return {
                            ...feature,
                            properties: {
                                ...feature.properties,
                                customId: getUniqueId()
                            }
                        };
                    }
                    const featureAny = feature as any;
                    const props = featureAny.properties || {};
                    return {
                        ...feature,
                        id: featureAny.id || props.id || getUniqueId(),
                        properties: {
                            ...props,
                            customId: props.customId || getUniqueId()
                        }
                    };
                })
            }
            // Modo selección: cada polígono de un MultiPolygon pasa a ser una feature (hermanas por `parentId`)
            const normalizedGeoJson = this.selectionEditing
                ? normalizeFeatureCollection(updatedGeoJson)
                : updatedGeoJson;

            // Ensure all split features have customId
            normalizedGeoJson.features.forEach((feature: any) => {
                if (!feature.properties.customId) {
                    feature.properties.customId = getUniqueId();
                }
            });

            this.googleMap.data.addGeoJson(normalizedGeoJson, {
                idPropertyName: 'customId'
            });
            this.googleMap.data.forEach((feature) => {
                feature.setProperty('layerType', 'polygon');
            });
            
        
            this.addGeoJsonHoverListener();
            this.addGeoJsonClickEvent();
            this.addBoundsChangedListener();
            this.addStylesToAllFeatures();
            this.fitMapToCurrentFeatures();
            await this.saveCurrentDataLayer();
            this.boundsChanged = true;
            this.emitCurrentGeometryIfChanged(true);
            this.addIndexTagsToAllPolygons();

        } catch(e){
            // console.log("add geojson error", e);
        }
    }

    //Ocultar/Mostrar etiquetas de los poligonos
    togglePolygonTagsVisibility(areVisible: boolean) {
        if (areVisible) {
            this.addIndexTagsToAllPolygons();
        } else {
            this.clearPolygonLabels();
        }
    }

    getFeatureIndexInSelectedRanchs(feature: google.maps.Data.Feature): number {
    const targetId = feature.getProperty('feature_unique_id') || feature.getProperty('id');

    return this.selectedRanchs.findIndex(item => {
        const itemId = item.getProperty('feature_unique_id') || item.getProperty('id');
        return itemId === targetId;
    });
}

    addFeatureToSelectedOnes(selectedFeature: google.maps.Data.Feature){
        selectedFeature.setProperty('selected', true);
        let self = this;
        self.googleMap.data.overrideStyle(
            selectedFeature,
            self.highlightSelection ? self.highlightedSelectionStyles : self.selectedPolylineOptions
        );
            
        let indexRanchInSelectedRanchs = this.getFeatureIndexInSelectedRanchs(selectedFeature);
        
            if(indexRanchInSelectedRanchs == -1){
                self.selectedRanchs.push(selectedFeature);
                self.exportSelectedRanchs();
            }
    }

    getPolygonsFromFeature(selectedFeature) {
        return selectedFeature.getGeometry().getArray();
    }

    getFeatureById(featureId: number | string): google.maps.Data.Feature {
        return this.googleMap.data.getFeatureById(featureId) as google.maps.Data.Feature;
    }

    triggerEvent(eventName: string, data) {
        google.maps.event.trigger(this.googleMap.data, eventName, data);
    }

    resetFeatureStyles(feature: google.maps.Data.Feature){
        if (this.highlightSelection) {
            // Vuelve al estilo base (no seleccionado) del tipo de mapa actual.
            this.googleMap.data.revertStyle(feature);
            return;
        }
        this.googleMap.data.overrideStyle(feature, this.defaultPolygonStyles);
    }

    updateFeatureProp(feature: google.maps.Data.Feature, name: string, value:boolean): void{
        feature.setProperty(name, value)
    }   

    unselectFeature(feature: google.maps.Data.Feature) {
        let ranchIsInSelectedRanchs = this.getFeatureIndexInSelectedRanchs(feature);
        if(ranchIsInSelectedRanchs != -1){
            this.selectedRanchs.splice(ranchIsInSelectedRanchs, 1);
        }
        this.updateFeatureProp(feature, 'selected', false)
        this.resetFeatureStyles(feature);
    }
    
    resetVisualization() {
        this.cancelEditPolygon();
        if(this.previousBounds){
            this.setCenter(this.previousBounds);
            this.fitBounds(this.previousBounds);
        }
    }

    getBoundsFromCoordsList(coords) {
        let bounds = new google.maps.LatLngBounds();
        coords.forEach(coord => {
            if (coord !== undefined) {
                bounds.extend(coord);
            }
        });
        return bounds;
    }


    setCenter(bounds: google.maps.LatLngBounds) {
        if(!bounds || !this.googleMap || !bounds.getCenter){
            return
        }

        this.googleMap.setCenter(bounds.getCenter());
    }

    setZoom(zoom: number) {
        this.googleMap.setZoom(zoom);
    }

    getFitBoundsPadding(): google.maps.Padding {
        const padding: google.maps.Padding = {
            top: 72,
            right: 64,
            bottom: 48,
            left: 40,
        };
        if (this.legendUrl) {
            const legendEl = document.querySelector('.legend-overlay') as HTMLElement | null;
            const legendHeight = legendEl ? legendEl.getBoundingClientRect().height : 52;
            padding.bottom = Math.round(legendHeight) + 28;
        }
        return padding;
    }

    fitBounds(bounds: google.maps.LatLngBounds) {
        if(!bounds || !this.googleMap){
            return
        }
        if (this.tenant && !this.tenant.map.fitBoundsPadding) {
            this.googleMap.fitBounds(bounds);
            return;
        }
        this.googleMap.fitBounds(bounds, this.getFitBoundsPadding());
    }

    fitMapToCurrentFeatures() {
        if (!this.googleMap || !this.googleMap.data || !this.areFeaturesInMap()) {
            return;
        }

        let bounds: google.maps.LatLngBounds | null = null;
        this.googleMap.data.forEach((feature: google.maps.Data.Feature) => {
            bounds = getExtendedBoundsFromFeature(feature, bounds);
        });

        if (!bounds) {
            return;
        }

        this.setCenter(bounds);
        this.fitBounds(bounds);
    }

    //Borro todos los features del data layer
    removeFeaturesFromDataLayer() {
        if(this.googleMap && this.googleMap.data){
            this.googleMap.data.forEach(feature => {
                this.googleMap.data.remove(feature);
            })
        }
    }


    mounted() {
        this.addCtrlListeners();
        let self = this;
        EventBus.$on(TypeEventBus.CANCEL_POLYGON_EDITION, (value) => {
            self.cancelPolygon();
        })
        this.$nextTick(() => {
            self.$emit('map-is-visible');
        });
        this.setupGeometryWatcher();
    }

    emitCurrentGeometryIfChanged(forceEmit = false) {
        if (!this.googleMap || !this.initialDataLayer) {
            return;
        }
        let self = this;

        this.googleMap.data.toGeoJson((currentGeoJson) => {
            const currentString = JSON.stringify(currentGeoJson);
            const initialString = JSON.stringify(this.initialDataLayer);
            const lastComparedString = JSON.stringify(this.lastComparedGeoJson || '{}');

            if (forceEmit || (currentString !== initialString && currentString !== lastComparedString)) {
                const coords = self.getCoordsFromDataLayer();
                
                self.modifiedCounter++;
                if(this.modifiedCounter > 1){
                    self.$emit('geometry-modified', coords, true);
                }else{
                    self.$emit('geometry-modified', coords);
                }

                self.lastComparedGeoJson = JSON.parse(currentString);
                self.addIndexTagsToAllPolygons();
            }
        });
    }

    //Metodo para asignarle un indice a cada poligono del mapa, requisito del cliente
    addIndexTagsToAllPolygons() {
        if(!this.polygonsTagsAreVisible) return
        
        let self = this;
        this.clearPolygonLabels();
        let currentIndex = 0;
        this.$nextTick(() => {
            this.googleMap.data.forEach((feature: google.maps.Data.Feature) => {
            currentIndex = createPolygonLabels(
                feature,
                self.googleMap,
                self.markersPositions,
                currentIndex
            );
            });
        });
    }


    clearPolygonLabels() {
        this.markersPositions.forEach(marker => marker.map = null);
        this.markersPositions = [];
    }

    setupGeometryWatcher() {
        
        setInterval(() => {
            this.emitCurrentGeometryIfChanged();
        }, 1000);
    }

    onMeasureToolEnd() {
        if (this.measureToolFallback) {
            this.measureToolFallback.onMeasureEnd();
        }
    }

    onMapInitialized(){
        this.mapMounted = true;
        this.drawDepartmentsLayer();

        // Satélite si lo pide el padre (`mapTypeId`) o, sin prop, el tenant (`features.satelliteMapByDefault`).
        if (this.getMapType === google.maps.MapTypeId.HYBRID) {
            this.googleMap.setMapTypeId(google.maps.MapTypeId.HYBRID);
            this.refreshPolygonStyles();
            this.mapIsSatellital = true;
        }

        this.setMultipolygon();
        this.emitMapIsSatellital();

        this.googleMap.addListener('maptypeid_changed', () => {
            this.refreshPolygonStyles();
            this.mapIsSatellital = this.googleMap.getMapTypeId() === 'hybrid';
            this.emitMapIsSatellital();
        });

        this.measureToolFallback = new MeasureToolMapFallback({
            map: this.googleMap,
            getMeasureTool: () => {
                const mapLoader = this.$refs.customMap as any;
                return mapLoader && mapLoader.measureTool ? mapLoader.measureTool : null;
            },
            isEditPolygon: () => this.editPolygon,
            isDeleteMode: () => this.deleteMode,
            isOverPolyVertex: imOverPolyVertex,
        });
        this.measureToolFallback.attach();

        setTimeout(() => {
            this.setPreselectedPolygons();
            // Mapa listo con los polígonos cargados: el padre puede sincronizar su selección.
            this.$emit('map-ready');
        }, 0);
    }

    emitMapIsSatellital() {
        this.$emit('map-is-satellital-changed', this.mapIsSatellital);
    }

    setMultipolygon(){
        if(this.multiPolygon){
            this.addGeoJson(this.multiPolygon);
        }
    }

    setPreselectedPolygons(){
        if(this.selectedItems.length > 0){
            this.selectFeaturesFromIds(this.selectedItems.map(item => item['feature_unique_id']));
        }
    }

   

    addNewPolygon(){
        if(this.areFeaturesInMap()){
            this.showModalEditPolygon();
        }else{
            this.enableMapDrawingTools();
        }
       
    }

    async enableMapDrawingTools() {
        let self = this;
        await this.saveCurrentDataLayer();
        let polygonType: any = ['POLYGON'];
        this.drawingManager = new google.maps.drawing.DrawingManager({
            drawingMode: google.maps.drawing.OverlayType.POLYGON,
            drawingControl: true,
            drawingControlOptions: {
                position: google.maps.ControlPosition.TOP_CENTER,
                drawingModes: polygonType
            }
        });
        this.drawingManager.setMap(this.googleMap);

        google.maps.event.addListener(this.drawingManager, 'polygoncomplete', function(newPolygon: google.maps.Polygon) {
            self.handleNewHandWrittenPolygon(newPolygon);
            newPolygon.setMap(null);
            self.visibilityAddPolygonModal(true);
            setTimeout(() => {
                self.addIndexTagsToAllPolygons();
            }, 300)
        });
    }

    removeDrawingTools() {
        let self = this;
        if(this.drawingManager !== null){
            this.drawingManager.setDrawingMode(null);
            this.drawingManager.setMap(null);
        }
        this.removeEditableFromAllFeatures()
    }

    cancelEditPolygon() {
        if(!this.editPolygon){
            return
        }
        this.removeVertexDeleteRightclickListener();
        this.editPolygon = false;
    }

    
    setPreviousDataLayer() {
        //Cuando se cancela la edicion, restauro el datalayer guardado previamente
        if (!this.initialDataLayer) {
            console.warn('No hay estado inicial guardado para restaurar.');
            return;
        }

        // Limpia el `dataLayer` actual
        this.googleMap.data.forEach((feature) => {
            this.googleMap.data.remove(feature);
        });

        // Restaura las geometrías iniciales
        this.googleMap.data.addGeoJson(this.initialDataLayer);
    }

    cancelPolygon() {
        this.cancelEditPolygon();
        this.removeDrawingTools();
        this.visibilityAddPolygonModal(false);
        if (this.selectionEditing) {
            this.exitDeleteMode();
        }
        this.setPreviousDataLayer();
        this.userCancelPolygon();
        EventBus.$emit(TypeEventBus.HAND_WRITING_DISABLED_BUTTON, false);
        if(this.modifiedCounter == 1){
            this.modifiedCounter = 0;
        }
        this.emitCurrentGeometryIfChanged(true)

    }

    async onSavePolygon() {   
        this.editPolygon = false;
        this.removeVertexDeleteRightclickListener();
        this.visibilityAddPolygonModal(false);
        this.renormalizeDataLayer();
        await this.saveCurrentDataLayer();
        this.savePolygon();
        this.removeDrawingTools();
        this.addGeoJsonHoverListener();
    }

    removeEditableFromAllFeatures(){
        let self = this;
        this.googleMap.data.forEach(feature => {
            self.updateFeatureEditableProp(feature, false);
        })
    }


    savePolygon() {
        let coords = this.getCoordsFromDataLayer();
        EventBus.$emit(TypeEventBus.HAND_WRITING_DISABLED_BUTTON, false);
        this.onPolygonSave(coords);
        this.saveCurrentDataLayer();
        if (this.contextMenuEditing) {
            this.fitMapToCurrentFeatures();
        }
    }


    selectFeaturesFromIds(
        idsToSelect: number[], 
        exportRanchs: boolean = true,
        resetSelection = true
    ){
        let self = this;
        if (resetSelection) {
            this.googleMap.data.forEach((feature: google.maps.Data.Feature) => {
                if (feature.getProperty('selected')) {
                    self.unselectFeature(feature);
                }
            });
            self.selectedRanchs = [];
        }

        
        this.selectedRanchs = [];
        self.googleMap.data.forEach(function(feature: google.maps.Data.Feature) {
            let mapFeatureId = feature.getProperty('id');
            
            if(!mapFeatureId){
                mapFeatureId = feature.getProperty('feature_unique_id');
            }

            if(mapFeatureId){
                let featureIdIsSelectable = idsToSelect.findIndex(id => id == mapFeatureId) > -1
                if (featureIdIsSelectable) {
                    self.addFeatureToSelectedOnes(feature);
                }else{
                    self.unselectFeature(feature);
                }
            }
        })
        if(exportRanchs){
            self.exportSelectedRanchs();
        }
    }

    focusPolygonInMapById(id: number){
        let self = this;
        let bounds = new google.maps.LatLngBounds();
        self.googleMap.data.forEach(function(feature: google.maps.Data.Feature) {
            let mapFeatureId = feature.getProperty('id');
            if (id == mapFeatureId) {
                bounds = getExtendedBoundsFromFeature(feature, bounds);
            }
        })

        self.setCenter(bounds);
        self.fitBounds(bounds);
    }

    /**
     * "Buscar por punto": marca el punto (reemplaza el marcador anterior) y centra el mapa ahí con `zoom`.
     * El marcador no está en la capa de datos, así que no se guarda, no se selecciona y no afecta la edición.
     */
    showSearchPoint(point: LatLngPoint, zoom: number = SEARCH_POINT_ZOOM) {
        if (!this.googleMap) return;
        this.clearSearchPoint();
        this.searchPointMarker = new google.maps.marker.AdvancedMarkerElement({
            position: point,
            map: this.googleMap,
            zIndex: 100,
            title: `${point.lat}, ${point.lng}`,
            content: customPinElement({
                backgroundColor: '#E53935',
                borderColor: '#B71C1C',
                icon: customIconForMarker({
                    glyph: '<i class="mdi mdi-crosshairs-gps" style="color: white; font-size: 16px;"></i>',
                    glyphColor: 'white',
                }),
            }),
        });
        this.googleMap.panTo(point);
        this.googleMap.setZoom(zoom);
    }

    clearSearchPoint() {
        if (this.searchPointMarker) {
            this.searchPointMarker.map = null;
            this.searchPointMarker = null;
        }
    }


    @Emit()
    onSelectedRanchs(){
        return this.selectedRanchs
    }

    exportSelectedRanchs() {
        this.onSelectedRanchs();
    }

    setGroundOverlayInMap(
        imageUrl: string,
        imageBounds: google.maps.LatLngBounds | Bounds,
        centerVisualization?: boolean
    ) {
        if (!this.mapMounted || !this.googleMap) return;
        const bounds = this.satellitalOverlays.show(this.googleMap, imageUrl, imageBounds);
        if (centerVisualization && bounds) this.setCenter(bounds);
    }

    hideGroundOverlayFromMap(url: string) {
        this.satellitalOverlays.hide(url);
    }

    hideGroundOverlaysFromMap() {
        this.satellitalOverlays.hideAll();
    }

    setGroundOverlayOpacity(url: string, opacity: number) {
        this.satellitalOverlays.setOpacity(url, opacity);
    }

    clearListeners(){
        if (this.hoverListener) {
            this.hoverListener.remove();
        }
        if (this.leaveListener) {
            this.leaveListener.remove();
        }
        if (this.hoverTimer !== null) {
            clearTimeout(this.hoverTimer);
        }
        this.removeDeleteModeListeners();
    }

    @Destroyed
    clean(): void {
        this.hideGroundOverlaysFromMap();
        if (this.measureToolFallback) {
            this.measureToolFallback.detach();
            this.measureToolFallback = null;
        }
        this.exportSelectedRanchs();
        this.clearPolygonLabels();
        this.clearListeners();
        this.clearSearchPoint();
        this.removeNoteSelectionListeners();
    }

    //NOTAS GEOREFERENCIADAS
    toggleRanchNotesMarkers(visible: boolean) {
        this.closeModalRanchNoteInfo();
        if (!this.ranchNoteMarkers || this.ranchNoteMarkers.length === 0) return;

        this.ranchNoteMarkers.forEach(marker => {
            marker.map = visible ? this.googleMap : null;
        });
    }

    displayRanchNotesMarkers(ranchNotesWithLocation: Array<object>) {
        this.removeRanchNotesMarkers();
        let self = this;

        if(ranchNotesWithLocation.length === 0) return

        this.addNoteSelectionListeners();

        ranchNotesWithLocation.forEach(point => {
            // Sampling dots only when enriched under CLAIM (sampling_marker_color set)
            const isSampling =
                point['sampling'] != null && !!point['sampling_marker_color'];
            const markerContent = isSampling
                ? this.createSamplingNoteMarkerContent(
                    point['sampling_marker_color'] || '#9E9E9E'
                  )
                : this.createRegularNoteMarkerContent();

            // Wrapper para resaltar (escala/anillo/opacidad) sin tocar el color del marcador
            const wrapper = document.createElement('div');
            wrapper.className = `note-marker ${isSampling ? 'note-marker--dot' : 'note-marker--pin'}`;
            wrapper.appendChild(markerContent);

            const marker = new google.maps.marker.AdvancedMarkerElement({
                position: point['location'],
                map: this.googleMap,
                zIndex: 99,
                content: wrapper,
            });

            const noteId = point['id'];
            marker.addListener('click', () => {
                // Primer click selecciona; click sobre la nota ya seleccionada abre el detalle
                if (noteId == null || noteId === self.selectedNoteId) {
                    self.openRanchNoteInfoModal(point);
                } else {
                    self.selectNote(noteId);
                }
            });

            this.ranchNoteMarkers.push(marker);
            if (noteId != null) this.ranchNoteMarkersById.set(noteId, marker);
        });

        this.applyNoteMarkersHighlight();
    }

    applyNoteMarkersHighlight() {
        const hasSelection =
            this.selectedNoteId != null && this.ranchNoteMarkersById.has(this.selectedNoteId);

        this.ranchNoteMarkersById.forEach((marker, id) => {
            const el = marker.content as HTMLElement;
            const isSelected = id === this.selectedNoteId;
            const isHovered = id === this.hoveredNoteId && !isSelected;
            el.classList.toggle('note-marker--selected', isSelected);
            el.classList.toggle('note-marker--hovered', isHovered);
            el.classList.toggle('note-marker--dimmed', hasSelection && !isSelected && !isHovered);
            marker.zIndex = isSelected ? 1000 : isHovered ? 999 : 99;
        });
    }

    panToNoteMarkerIfOutOfView(noteId: number) {
        const marker = this.ranchNoteMarkersById.get(noteId);
        if (!marker || !marker.position || !this.googleMap) return;
        const position = marker.position as google.maps.LatLng | google.maps.LatLngLiteral;
        const bounds = this.googleMap.getBounds();
        if (bounds && bounds.contains(position)) return;
        this.googleMap.panTo(position);
    }

    onNoteSelectionKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && this.selectedNoteId != null) this.selectNote(null);
    }

    addNoteSelectionListeners() {
        if (this.noteSelectionMapListener || !this.googleMap) return;
        // Click en zona vacía del mapa quita la selección
        this.noteSelectionMapListener = this.googleMap.addListener('click', () => {
            if (this.selectedNoteId != null) this.selectNote(null);
        });
        window.addEventListener('keydown', this.onNoteSelectionKeydown);
    }

    removeNoteSelectionListeners() {
        if (this.noteSelectionMapListener) {
            this.noteSelectionMapListener.remove();
            this.noteSelectionMapListener = null;
        }
        window.removeEventListener('keydown', this.onNoteSelectionKeydown);
    }

    createRegularNoteMarkerContent() {
        return customPinElement({
            scale: 0.75,
            backgroundColor: '#003781',
            borderColor: '#002456',
            icon: customIconForMarker({
                glyph: '<i class="mdi mdi-note-text" style="color: white; font-size: 14px;"></i>',
                glyphColor: 'white',
            }),
        });
    }

    /** Sampling notes: filled circle with white border (no document icon) */
    createSamplingNoteMarkerContent(color: string = '#9E9E9E'): HTMLDivElement {
        const markerContent = document.createElement("div");
        markerContent.style.width = "24px";
        markerContent.style.height = "24px";
        markerContent.style.backgroundColor = color;
        markerContent.style.border = "2px solid white";
        markerContent.style.borderRadius = "50%";
        markerContent.style.boxShadow = "0 0 2px rgba(0,0,0,0.35)";
        markerContent.style.boxSizing = "border-box";
        return markerContent;
    }

    openRanchNoteInfoModal(noteInfo: object) {
        this.ranchNoteInfo = noteInfo;
        this.visibleModalRanchNoteInfo = true;
    }

    closeModalRanchNoteInfo() {
        this.visibleModalRanchNoteInfo = false;
        this.ranchNoteInfo = null;
    }

    removeRanchNotesMarkers(){
        this.closeModalRanchNoteInfo();
        this.ranchNoteMarkers.forEach(marker => {
            marker.map = null;
        });
        this.ranchNoteMarkers = [];
        this.ranchNoteMarkersById.clear();
    }
}
